<template>
  <form
    ref="form"
    :action="submitUrl"
    method="GET"
    role="search"
    :aria-label="ariaLabel"
    :class="allClasses"
    @reset.prevent.stop="onReset"
  >
    <input
      v-for="(value, key) in params"
      :key="key"
      type="hidden"
      :name="key"
      :value="value"
      :data-test-id="`search-form-param-${key}`"
    >
    <div class="search-form__input-wrapper">
      <input
        ref="input"
        :value="viewInputText"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        class="search-form__input"
        type="search"
        name="q"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        :autofocus="hasAutoFocus"
        required
        data-test-id="search-form-input"
        @input="onInput"
        @keydown="onKeydown"
        @focus="onFocus"
        @blur="onBlur"
      >
    </div>
    <div class="search-form__actions">
      <EIconButton
        class="search-form__reset"
        :class="{ 'search-form__reset--hidden': !resetVisible }"
        type="reset"
        icon="close"
        icon-class="search-form__reset-icon"
        aria-label="Reset"
        data-test-id="search-form-reset"
      />
      <EIconButton
        class="search-form__submit"
        type="submit"
        icon="search"
        icon-class="searchf-form__submit-icon"
        :icon-size="variant === 'compact' ? 's' : 'm'"
        aria-label="Submit"
        data-test-id="search-form-submit"
      />
    </div>
    <div v-if="displaySuggestionsList" class="search-form__suggestions">
      <ESuggestionList
        class="search-form__suggestions-list"
        :input-text="inputText"
        :autocomplete-url="autocompleteUrl"
        :market="market"
        :top-section-only="false"
        :variant="variant"
        data-test-id="search-form-suggestions"
        @updateInputText="onSuggestionListInput"
        @triggerSuggestionSearch="onSuggestionListSearch"
        @visibilityChange="onSuggestionListVisibilityChange"
      />
    </div>
    <slot name="additional-fields" />
  </form>
</template>

<script>
import { mapState } from 'vuex';
import { getUrlQuery } from '~~/common/js/universal/search';
import EIconButton from '~~/common/vue/components/icon-button';
import { REQUEST_DEBOUNCE_DELAY } from '~~/common/js/constants/events';
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import ESuggestionList from './suggestion-list';

export const searchFormVariants = [
  'default',
  'compact',
];

export default {
  name: 'ESearchForm',
  components: {
    ESuggestionList,
    EIconButton,
  },
  mixins: [classes],
  props: {
    /**
     * Supported variants are `default` and `compact`
     */
    variant: {
      type: String,
      default: 'default',
      validator: (variant) => searchFormVariants.includes(variant),
      cssClass: true,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    autocompleteUrl: {
      type: String,
      default: '',
    },
    // eslint-disable-next-line vue/require-default-prop
    market: ESuggestionList.props.market,
    placeholder: {
      type: String,
      default: '',
    },
    action: {
      type: String,
      required: true,
    },
    hasAutoFocus: {
      type: Boolean,
      default: false,
    },
    /**
     * if enabled, the form will render with a pre-filled query
     * taken from the `q` URL parameter
     */
    prefillQuery: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      viewInputText: '',
      inputText: '',
      suggestionsVisible: false,
    };
  },
  computed: {
    ...mapState('user', ['autoSuggest']),
    allClasses() {
      const allClasses = [...this.classes];
      if (this.suggestionsVisible) {
        allClasses.push(getModifierClass('search-form', 'suggestions-visible'));
      }
      return allClasses;
    },
    query() {
      // $route is not set in the web-extension
      return this.$route?.query.originalQuery || this.$route?.query.q || '';
    },
    resetVisible() {
      return !!this.inputText;
    },
    params() {
      // TODO: allow params to preserve to come from props
      const verticalParams = [
        'freshness',
        'resolution',
        'videoLength',
        'size',
        'color',
        'imageType',
        'freshness',
        'aspect',
        'license',
      ];
      const params = [
        ...verticalParams,
        'canary',
        'mocked',
        'forceProvider',
        'sfs',
        'sfn'
      ];
      const query = this.$route?.query || {};
      return getUrlQuery(query, params);
    },
    submitUrl() {
      if (!this.$route.hash) {
        return this.action;
      }
      // TODO: make this behaviour customizabile via props
      // TODO: duplicates FILTERS_KEY exported from search/common/js/universal/hash-params
      const filtersKey = 'f';
      const hashParams = new URLSearchParams(this.$route.hash.replace('#', ''));
      const filtersParam = hashParams.get(filtersKey);
      // create a new url params object with only `filters` key
      const hash = new URLSearchParams({ [filtersKey]: filtersParam });
      return `${this.action}#${hash}`;
    },
    displaySuggestionsList() {
      return this.autoSuggest && this.autocompleteUrl;
    },
  },
  created() {
    if (this.prefillQuery) {
      this.setInputTexts(this.query);
    }
  },
  methods: {
    setInputTexts(value) {
      this.viewInputText = value;
      this.inputText = value;
    },
    onInput({ target: { value } }) {
      this.setInputTexts(value);
    },
    onKeydown({ key }) {
      if (!this.displaySuggestionsList) {
        return;
      }
      const keyMap = {
        ArrowDown: 'down',
        ArrowUp: 'up',
        Escape: 'esc',
      };
      const name = keyMap[key];
      if (name) {
        this.$emit('onKeyDown', name);
      }
    },
    onFocus() {
      if (!this.displaySuggestionsList) {
        return;
      }
      this.$emit('getSuggestions');
    },
    onBlur(event) {
      if (!this.displaySuggestionsList) {
        return;
      }
      // TODO Refactor this logic to avoid coupling with classname
      const isSuggestionLink = event.relatedTarget ?
        event.relatedTarget.className === 'suggestion-link' :
        false;
      if (!isSuggestionLink) {
        this.$emit('clearSuggestions');
      }
      this.suggestionsVisible = false;
    },
    onSuggestionListInput(value) {
      this.viewInputText = !value ? this.inputText : value;
    },
    onSuggestionListSearch(value) {
      this.onSuggestionListInput(value);
      setTimeout(() => {
        this.$refs.form.submit();
      }, REQUEST_DEBOUNCE_DELAY);
    },
    onSuggestionListVisibilityChange(isVisible) {
      this.suggestionsVisible = isVisible;
    },
    onReset() {
      this.setInputTexts('');
      this.$refs.input.focus();
    },
  },
};
</script>

<style lang="scss" scoped>

.search-form {
  display: flex;
  z-index: $z-index-l;
  margin: 0 auto;
  border-radius: $border-radius-m;
  background-color: $white;
  border: solid $border-width $color-border;
  position: relative;
  justify-content: space-around;
  align-items: center;
  text-align: left;
  height: $space-3l;
}

.search-form__input-wrapper {
  flex: 1 1 auto;
  height: 100%;
  overflow: hidden;

  @include mobile-l {
    padding-left: $space-m;
  }
}

.search-form__actions {
  display: flex;
  flex-direction: row;
  flex: 0 1 auto;
}

.search-form__reset--hidden {
  visibility: hidden;
}

.search-form__input {
  border: none;
  width: 100%;
  color: $color-text-primary;

  &::placeholder {
    color: $color-text-secondary;
    opacity: 1;
    text-overflow: ellipsis;
  }

  &::-ms-clear {
    display: none;
  }

  &:focus {
    outline: none;
  }

  &:required {
    box-shadow: none;
  }

  &:invalid {
    box-shadow: none;
  }
}

.search-form--variant-default {
  $height: $space-3l;
  $medium-height: 58px;

  max-width: 683px;

  @include tablet {
    height: $medium-height;
  }

  .search-form__actions {
    display: flex;
    flex-direction: row;
    flex: 0 1 auto;
    padding-right: $space-s;

    @include mobile-l {
      padding-right: $space-1s;
    }

    @include tablet {
      padding-right: $space-s;
    }
  }

  .search-form__input-wrapper {
    padding-left: $space-s;

    @include tablet {
      padding-left: $space-l;
    }
  }

  .search-form__reset,
  .search-form__submit {
    height: $height;

    @include tablet {
      height: $medium-height;
    }
  }

  .search-form__input {
    font-size: $font-l;
    height: $height;

    @include tablet {
      font-size: $font-2l;
      height: $medium-height;
    }
  }
}

.search-form--variant-compact {
  $height: $space-3l;

  .search-form__actions {
    padding: 0 $space-m;
  }

  .search-form__input-wrapper {
    padding-left: $space-m;
  }

  .search-form__reset,
  .search-form__submit {
    padding: 0;
    height: auto;
    width: auto;
  }

  .search-form__input {
    font-size: $font-m;
    // explicitly setting the height because Chrome adds a random 2px
    // to the input field's internal height
    height: $height - $space-2s;
    padding: #{$space-s + $space-2s} 0;
  }

  .search-form__reset {
    margin-right: $space-s;
  }
}

.search-form--suggestions-visible {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
</style>

<style lang="scss">
$search-icon-width: 27px;
$reset-icon-width: 12px;
$reset-icon-medium-width: 14px;

.search-form__reset-icon {
  transform: scale($reset-icon-width / $reset-icon-medium-width);

  @include tablet {
    transform: scale($scale-m);
  }
}

.search-form--variant-default {
  .search-form__submit-icon {
    width: $space-l;

    @include tablet {
      width: $search-icon-width;
    }
  }
}
</style>

<docs>

Default variant:

```js
<ESearchForm
  variant="default"
  search-url="https://www.ecosia.org/search"
  autocomplete-url="http://localhost" />
```

Compact variant with placeholder:

```js
<ESearchForm
  variant="compact"
  search-url="https://www.ecosia.org/search"
  autocomplete-url="http://localhost"
  placeholder="Search the web to plant trees" />
```
</docs>
